import { Utils, StringUtils, ArrayUtils, Logger, engineComponent } from "neo-common";
import { ComponentTypes } from "../../ComponentTypes";
import { StageController } from "../../StageController";
import { Tweens, ITweenable, TweensAttributes } from "../../Effects/Tweens";
import { IElement } from "../Interfaces/IElement";
import { ShapeElement } from "./ShapeElement";
import { Attributes } from "../../../Utils/Attributes";
import { TextElement } from "../Texts/TextElement";
import { CustomUtils } from "../../../Utils/CustomUtils";

/**
 * ImageElement class is class which creates holder for image from resources. This image will be rendered to canvas.
 * Example:
 *   <ImageElement Class="ImageElement" Image="[imageId]" X="0" Y="0" />
 * @class ImageElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ImageElement extends PIXI.Sprite implements IElement, ITweenable {
    static DynamicTint: string = "000000"; // Todo Transform this into array, e.g. DynamicTint['dynamic_symbols'] = '000000' ? And use it as attribute with this style: Tint="dynamic_symbols", Tint="dynamic2",..

    private _uid: string;
    private _cloned: boolean;
    private __destroyed: boolean;

    maskSprite: PIXI.Sprite;
    maskGraphics: ShapeElement;

    usedInPool: boolean;

    static _destroyOptions: PIXI.DestroyOptions = {
        children: true,
    };

    zIndex: number = 0;

    protected myParent: any;

    constructor(texture?: PIXI.Texture) {
        super(texture);
        this.usedInPool = false;
        this._uid = `${this.componentName}_${Utils.generateUuid()}`;

        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                this._uid = PerfDebug.generateElementUID(this);
                PerfDebug.newElement(this, this.getDebugInfo());
            }
        }
    }

    public create(node: Element): void {
        this.createByNode(node);
        this.updateByNode(node);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public createByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(ImageElementAttributes.DEBUG) &&
                node.getAttribute(ImageElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        if (this.name == null) {
            this.name = node.nodeName;
        }

        if (node.hasAttribute(ImageElementAttributes.IMAGE)) {
            // get loaded image from resources
            this.texture = StageController.getTextureByResourceId(node.getAttribute(ImageElementAttributes.IMAGE));
        } else if (node.hasAttribute(ImageElementAttributes.FROM_FRAME)) {
            if (node.hasAttribute(ImageElementAttributes.FRAME)) {
                let frame = parseInt(node.getAttribute(ImageElementAttributes.FRAME));
                let frameStr = frame < 10 ? "0" + frame : frame;
                this.texture = StageController.getTextureByResourceId(
                    node.getAttribute(ImageElementAttributes.FROM_FRAME) + "00" + frameStr,
                );
            } else {
                this.texture = StageController.getTextureByResourceId(
                    node.getAttribute(ImageElementAttributes.FROM_FRAME),
                );
            }
        } else if (node.hasAttribute(ImageElementAttributes.ANIMATION)) {
            let frame: number = 0;
            if (node.hasAttribute(ImageElementAttributes.FRAME)) {
                frame = parseInt(node.getAttribute(ImageElementAttributes.FRAME));
            }
            let frameStr = frame < 10 ? "0" + frame : frame;

            this.texture = StageController.getTextureByResourceId(
                node.getAttribute(ImageElementAttributes.ANIMATION) + "00" + frameStr,
            );
        }

        if (this.texture) {
            if (node.hasAttribute(ImageElementAttributes.ANCHOR)) {
                let anchorPoints = node.getAttribute(ImageElementAttributes.ANCHOR).split(";");

                if (anchorPoints.length === 1) {
                    this.anchor.x = parseFloat(anchorPoints[0]);
                    this.anchor.y = parseFloat(anchorPoints[0]);
                } else if (anchorPoints.length === 2) {
                    this.anchor.x = parseFloat(anchorPoints[0]);
                    this.anchor.y = parseFloat(anchorPoints[1]);
                }
            }
        } else {
            if (DEBUG) {
                if (!(this.texture && this.texture.baseTexture)) {
                    //this.log('ERROR no Image with name: ' + node.getAttribute(ImageElementAttributes.IMAGE));
                    this.log("ERROR Image has no texture");
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(ImageElementAttributes.MASK)) {
                    let maskGraphics: ShapeElement;
                    if (this.myParent != null && this.myParent.children != null) {
                        let parentsChildren = this.myParent.children;
                        if (parentsChildren.length > 0) {
                            for (let i = parentsChildren.length - 1; i >= 0; i--) {
                                if (
                                    parentsChildren[i] != null &&
                                    parentsChildren[i] instanceof ShapeElement &&
                                    parentsChildren[i].name === next.nodeName
                                ) {
                                    maskGraphics = parentsChildren[i] as ShapeElement;
                                }
                            }
                        }
                    }

                    if (maskGraphics == null) {
                        this.maskGraphics = new ShapeElement();
                        this.maskGraphics.create(next);
                    } else {
                        this.maskGraphics = maskGraphics;
                    }

                    this.addChild(this.maskGraphics);
                    this.mask = this.maskGraphics;
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(ImageElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK)) {
            let image = StageController.getTextureByResourceId(node.getAttribute(ImageElementAttributes.BITMAP_MASK));

            if (image != null) {
                this.maskSprite = new PIXI.Sprite(image);
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_X)) {
                    this.maskSprite.position.x = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_X));
                }
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_Y)) {
                    this.maskSprite.position.y = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_Y));
                }
                if (!node.hasAttribute(ImageElementAttributes.BITMAP_MASK_SHOW)) {
                    this.addChild(this.maskSprite);
                    this.mask = this.maskSprite;
                }
            } else {
                if (DEBUG) {
                    this.log(
                        "ERROR" +
                            this.componentName +
                            " no image with name for mask: " +
                            node.getAttribute(ImageElementAttributes.BITMAP_MASK),
                    );
                }
            }
        }

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.createElement(this, this.getDebugInfo());
            }
        }
    }

    /**
     * @param node
     */
    public updateByNode(node: Element): ImageElement {
        if (!node) {
            this.logError("No node specified for element update!");
            return;
        }

        if (DEBUG) {
            if (
                node.hasAttribute(ImageElementAttributes.DEBUG) &&
                node.getAttribute(ImageElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        Tweens.removeTweens(this);

        this.visible = Attributes.assignBooleanAttribute(node, ImageElementAttributes.VISIBLE, this.visible);
        this.alpha = Attributes.assignFloatAttribute(node, ImageElementAttributes.ALPHA, this.alpha);
        this.blendMode = CustomUtils.getBlendMode(
            this.getBlendModeByString(
                Attributes.assignStringAttribute(node, ImageElementAttributes.BLEND_MODE, this.blendMode),
            ),
        );
        this.pivot.x = Attributes.assignIntAttribute(node, ImageElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, ImageElementAttributes.PIVOT_Y, this.pivot.y);

        if (node.hasAttribute(ImageElementAttributes.ROTATION)) {
            this.rotation = this.degrees2radians(parseFloat(node.getAttribute(ImageElementAttributes.ROTATION)));
        }

        if (node.hasAttribute(ImageElementAttributes.START_ROTATION)) {
            if (node.getAttribute(ImageElementAttributes.START_ROTATION) === ImageElementAttributes.START_ROTATION) {
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        this.position.x = Attributes.assignIntAttribute(node, ImageElementAttributes.X, this.position.x);
        this.position.y = Attributes.assignIntAttribute(node, ImageElementAttributes.Y, this.position.y);

        if (node.hasAttribute(ImageElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(ImageElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(ImageElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(ImageElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(ImageElementAttributes.SCALE));
        }

        if (node.hasAttribute(ImageElementAttributes.SKEW_X)) {
            this.skew.x = parseFloat(node.getAttribute(ImageElementAttributes.SKEW_X));
        }

        if (node.hasAttribute(ImageElementAttributes.SKEW_Y)) {
            this.skew.y = parseFloat(node.getAttribute(ImageElementAttributes.SKEW_Y));
        }

        if (node.hasAttribute(ImageElementAttributes.SKEW)) {
            this.skew.x = parseFloat(node.getAttribute(ImageElementAttributes.SKEW));
            this.skew.y = parseFloat(node.getAttribute(ImageElementAttributes.SKEW));
        }

        if (node.hasAttribute(ImageElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(ImageElementAttributes.MASK)) {
                    let maskGraphics: ShapeElement;
                    if (this.myParent != null && this.myParent.children != null) {
                        let parentsChildren = this.myParent.children;
                        if (parentsChildren.length > 0) {
                            for (let i = parentsChildren.length - 1; i >= 0; i--) {
                                if (
                                    parentsChildren[i] != null &&
                                    parentsChildren[i] instanceof ShapeElement &&
                                    parentsChildren[i].name === next.nodeName
                                ) {
                                    maskGraphics = parentsChildren[i] as ShapeElement;
                                }
                            }
                        }
                    }

                    if (maskGraphics == null) {
                        this.maskGraphics = new ShapeElement();
                        this.maskGraphics.create(next);
                    } else {
                        this.maskGraphics = maskGraphics;
                    }

                    this.addChild(this.maskGraphics);
                    this.mask = this.maskGraphics;
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(ImageElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK)) {
            let image = StageController.getTextureByResourceId(node.getAttribute(ImageElementAttributes.BITMAP_MASK));

            if (image != null) {
                this.maskSprite = new PIXI.Sprite(image);
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_X)) {
                    this.maskSprite.position.x = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_X));
                }
                if (node.hasAttribute(ImageElementAttributes.BITMAP_MASK_Y)) {
                    this.maskSprite.position.y = parseFloat(node.getAttribute(ImageElementAttributes.BITMAP_MASK_Y));
                }
                if (!node.hasAttribute(ImageElementAttributes.BITMAP_MASK_SHOW)) {
                    this.addChild(this.maskSprite);
                    this.mask = this.maskSprite;
                }
            } else {
                if (DEBUG) {
                    this.log(
                        "ERROR" +
                            this.componentName +
                            " no image with name for mask: " +
                            node.getAttribute(ImageElementAttributes.BITMAP_MASK),
                    );
                }
            }
        }

        if (node.hasAttribute(ImageElementAttributes.TINT)) {
            const tint = node.getAttribute(ImageElementAttributes.TINT);

            switch (tint) {
                case "none":
                    this.tint = 0xffffff;
                    break;
                case "dynamic":
                    this.tint = parseInt(ImageElement.DynamicTint, 16);
                    break;
                default:
                    this.tint = parseInt(node.getAttribute(ImageElementAttributes.TINT), 16);
                    break;
            }
        }

        this.runPositionTween(node);
        this.runBezierTween(node);
        this.runScaleTween(node);
        this.runRotationTween(node);
        this.runAlphaTween(node);
        this.runTween(node);

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }

        return this;
    }

    /**
     * Runs the alpha tween
     * @method runAlphaTween
     * @param node
     * @returns {boolean}
     */
    public runAlphaTween(node: Element): boolean {
        if (!node.hasAttribute(ImageElementAttributes.ALPHA_TWEEN)) {
            return false;
        }

        let alphaTween: Array<number> = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.ALPHA_TWEEN));

        const alphas: Array<number> = alphaTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(node, ImageElementAttributes.ALPHA_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.ALPHA_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ImageElementAttributes.ALPHA_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.ALPHA_TWEEN_LOOP,
            null,
        );

        Tweens.alphaTween(this, { alphas, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the position tween
     * @method runPositionTween
     * @param node
     * @returns {boolean}
     */
    public runPositionTween(node: Element): boolean {
        if (
            (!node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_X) ||
                !node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_Y)) &&
            (!node.hasAttribute("PositionTweenToX") || !node.hasAttribute("PositionTweenToY")) &&
            (!node.hasAttribute("AbsolutePositionTweenToX") || !node.hasAttribute("AbsolutePositionTweenToY"))
        ) {
            return false;
        }

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        if (
            node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_X) &&
            node.hasAttribute(ImageElementAttributes.POSITION_TWEEN_Y)
        ) {
            let positionTweenPosition: string = Attributes.assignStringAttribute(
                node,
                ImageElementAttributes.POSITION_TWEEN_POSITION,
                null,
            );
            if (positionTweenPosition === ImageElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => value + this.position.x,
                );
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => value + this.position.y,
                );
            } else {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_X));
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.POSITION_TWEEN_Y));
            }
        } else if (node.hasAttribute("PositionTweenToX") && node.hasAttribute("PositionTweenToY")) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute("PositionTweenToX"));
            positionTweenX.unshift(this.position.x);

            positionTweenY = ArrayUtils.numberArray(node.getAttribute("PositionTweenToY"));
            positionTweenY.unshift(this.position.y);
        } else if (node.hasAttribute("AbsolutePositionTweenToX") && node.hasAttribute("AbsolutePositionTweenToY")) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute("AbsolutePositionTweenToX")).map(
                (value) => this.position.x + (value - this.getGlobalPosition().x),
            );
            positionTweenX.unshift(this.position.x);

            positionTweenY = ArrayUtils.numberArray(node.getAttribute("AbsolutePositionTweenToY")).map(
                (value) => this.position.y + (value - this.getGlobalPosition().y),
            );
            positionTweenY.unshift(this.position.y);
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ImageElementAttributes.POSITION_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );

        Tweens.positionTween(this, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     *
     * @param node
     */
    public runBezierTween(node: Element): boolean {
        if (
            !node.hasAttribute(ImageElementAttributes.BEZIER_TWEEN_X) ||
            !node.hasAttribute(ImageElementAttributes.BEZIER_TWEEN_Y)
        ) {
            return false;
        }

        let bezierX: Array<number> = null;
        let bezierY: Array<number> = null;
        let bezier: any = null;

        if (
            node.hasAttribute(ImageElementAttributes.BEZIER_TWEEN_X) &&
            node.hasAttribute(ImageElementAttributes.BEZIER_TWEEN_Y)
        ) {
            bezierX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.BEZIER_TWEEN_X));
            bezierY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.BEZIER_TWEEN_Y));
            bezier = { values: [] };

            for (let i = 0; i < Math.min(bezierX.length, bezierY.length); i++) {
                bezier.values.push({ x: bezierX[i], y: bezierY[i] });
            }

            if (node.hasAttribute(ImageElementAttributes.BEZIER_TWEEN_AUTOROTATE)) {
                let autorotate: string = node.getAttribute(ImageElementAttributes.BEZIER_TWEEN_AUTOROTATE);
                if (parseInt(autorotate) || StringUtils.parseBoolean(autorotate)) {
                    bezier["autoRotate"] = autorotate;
                } else {
                    let autorotateArray: Array<any> = autorotate.split(";");
                    if (autorotateArray.length == 5) {
                        // e.g. ["x","y","rotation",45,true]
                        let [a, b, c, d, e] = autorotateArray;
                        bezier["autoRotate"] = [];
                        bezier["autoRotate"].push(a);
                        bezier["autoRotate"].push(b);
                        bezier["autoRotate"].push(c);
                        bezier["autoRotate"].push(this.degrees2radians(parseInt(d)));
                        bezier["autoRotate"].push(e == "true");
                    }
                }
            }
        }

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.BEZIER_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.BEZIER_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ImageElementAttributes.BEZIER_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.BEZIER_TWEEN_LOOP,
            null,
        );

        Tweens.bezierTween(this, { bezier, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the rotation tween
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runRotationTween(node: Element): boolean {
        if (!node.hasAttribute(ImageElementAttributes.ROTATION_TWEEN)) {
            return false;
        }

        let rotationTween: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(ImageElementAttributes.ROTATION_TWEEN),
        );

        const degrees: Array<number> = rotationTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ImageElementAttributes.ROTATION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.ROTATION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ImageElementAttributes.ROTATION_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.ROTATION_TWEEN_LOOP,
            null,
        );

        Tweens.rotationTween(this, { degrees, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the scale tween
     *
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runScaleTween(node: Element): boolean {
        let scaleTweenX: Array<number>;
        let scaleTweenY: Array<number>;
        let scaleTween: Array<PIXI.Point>;
        let tweenValues: Array<PIXI.Point> = [];

        if (node.hasAttribute(ImageElementAttributes.SCALE_TWEEN_X)) {
            scaleTweenX = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SCALE_TWEEN_X));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE_TWEEN_Y)) {
            scaleTweenY = ArrayUtils.numberArray(node.getAttribute(ImageElementAttributes.SCALE_TWEEN_Y));
        }

        if (node.hasAttribute(ImageElementAttributes.SCALE_TWEEN)) {
            scaleTween = ImageElement._getPointsFromPointsAttribute(
                node.getAttribute(ImageElementAttributes.SCALE_TWEEN),
            ) as Array<PIXI.Point>;
        }

        if (scaleTween && scaleTween.length) {
            tweenValues = scaleTween;
        } else if (scaleTweenX && scaleTweenY) {
            if (scaleTweenX.length === scaleTweenY.length) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: scaleTweenY[i],
                    } as PIXI.Point;
                });
            }
        } else if (scaleTweenX) {
            tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                return {
                    x: scaleXValue,
                    y: this.scale.y,
                } as PIXI.Point;
            });
        } else if (scaleTweenY) {
            tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                return {
                    x: this.scale.x,
                    y: scaleYValue,
                } as PIXI.Point;
            });
        } else {
            return false;
        }

        const tweenEase: string = Attributes.assignStringAttribute(node, ImageElementAttributes.SCALE_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageElementAttributes.SCALE_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, ImageElementAttributes.SCALE_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageElementAttributes.SCALE_TWEEN_LOOP,
            null,
        );

        Tweens.scaleTween(this, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     *
     * @param {Element} node
     * @returns {boolean}
     */
    public runTween(node: Element): boolean {
        if (node.hasAttribute(TweensAttributes.TWEEN)) {
            let tweens: Array<string> = ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN));

            for (let i: number = 0; i < tweens.length; i++) {
                //return if there are no values to tween
                if (!node.hasAttribute(TweensAttributes.TWEEN_VALUES + i)) {
                    return false;
                }

                let tween: string = tweens[i];

                const tweenValues: Array<number> = this.formatTweenValues(
                    tween,
                    ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN_VALUES + i)),
                );
                const tweenDuration: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DURATION + i) || "120",
                );
                const tweenEase: Array<string> = ArrayUtils.stringArray(
                    node.getAttribute(TweensAttributes.TWEEN_EASE + i) || "linear",
                );
                const tweenDelay: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DELAY + i) || "0",
                );
                const tweenLoop: boolean = Attributes.assignBooleanAttribute(
                    node,
                    TweensAttributes.TWEEN_LOOP + i,
                    null,
                );

                if (tween === "scale") {
                    tween = "scale.x";
                    tweens[i] = "scale.y";
                    i--;
                }

                Tweens.customTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            }
        }

        return false;
    }

    /**
     * Formats values in case of characters "c/+/-".
     * @param tween
     * @param values
     */
    private formatTweenValues(tween: string, values: Array<string>): Array<number> {
        let tweenValues: Array<number> = [];

        if (tween === "scale") {
            tween = "scale.x";
        }
        let property: Array<string> = tween.split(".");
        let value: number;

        for (let i: number = 0; i < values.length; i++) {
            let current = values[i];
            switch (current[current.length - 1]) {
                case "c": //if we want current value
                    if (property.length == 1) {
                        value = this[property[0]];
                        if (property[0] === "rotation") {
                            value = this.radians2degrees(value);
                        }
                        tweenValues.push(value);
                    } else if (property.length == 2) {
                        tweenValues.push(this[property[0]][property[1]]);
                    }

                    break;
                case "+":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] + value;
                    }
                    tweenValues.push(value);
                    break;
                case "-":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] - value;
                    }
                    tweenValues.push(value);
                    break;
                default:
                    tweenValues.push(parseFloat(current));
            }
        }

        return tweenValues;
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ImageElement
     */
    clone(): ImageElement {
        let element: ImageElement = new ImageElement();
        element.removeChildren();

        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.anchor = this.anchor;
        element.tint = this.tint;
        element.blendMode = this.blendMode;
        element.name = this.name;

        if (this.texture != null) {
            element.texture = this.texture;
        }

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        if (this.maskGraphics != null) {
            // todo Add?
            element.maskGraphics = this.maskGraphics;
            element.addChild(this.maskGraphics);
            element.mask = this.maskGraphics;
        }

        element.markAsCloned(); // must be called last before return!

        return element;
    }

    /**
     *
     * @param options
     */
    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this);

        this.texture = null;
    }

    getDebugInfo(): string {
        if (DEBUG) {
            let debugInfoFn = this.debugInfo;
            return debugInfoFn ? debugInfoFn.call(this) : `${this.uid}\nName: [${this.name}]`;
        }
        return "";
    }

    debugInfo(): string {
        if (DEBUG) {
            let result = `${this.uid}\nName: [${this.name}]`;
            let imgurl = this.texture && this.texture.baseTexture ? this.texture.baseTexture.imageUrl : "";
            if (!StringUtils.isNullOrEmpty(imgurl)) {
                result += "\nUrl: " + imgurl;
            }
            return result;
        }
        return "";
    }

    /**
     * @property cloned - indicates if element was cloned or not.
     * @returns {boolean}
     */
    public get cloned(): boolean {
        return this._cloned;
    }

    public get destroyed(): boolean {
        return this.__destroyed;
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.assertDestroyed("destroy");

        if (Utils.isNullOrUndefined(options)) {
            options = ImageElement._destroyOptions;
        }

        this.internalDestroy(options);

        if (!Utils.isNullOrUndefined(this.children)) {
            super.destroy(options);
        }

        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.destroy(this);
            }
        }

        this.__destroyed = true;
    }

    protected assertDestroyed(method: string): void {
        if (DEBUG) {
            if (this.__destroyed) {
                throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
            }
        }
    }

    /**
     * Converts string from "points attribute" into array of points. E.g. "(1,6);(5,2);2;(2,4)". Point can be specified as single value for both axex.
     * @method _getPointsFromPointsAttribute
     * @return Array<PIXI.Points>
     */
    private static _getPointsFromPointsAttribute(pointsString: string): Array<{ x: number; y: number }> {
        if (!pointsString) {
            return [];
        }

        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) =>
                /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair],
            )
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    /**
     * Marks element as cloned
     */
    protected _markAsCloned(): void {
        this.markAsCloned();
    }

    /**
     * Marks element as cloned
     */
    markAsCloned(): void {
        this._assertDestroyed("markAsCloned()");
        this._cloned = true;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.clone(this);

                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    /**
     *
     * @param method
     * @private
     */
    protected _assertDestroyed(method: string): void {
        if (DEBUG) {
            if (this.__destroyed) {
                throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
            }
        }
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    removeAndDestroyChildren(): void {
        this._removeAndDestroyChildren();
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    protected _removeAndDestroyChildren(): void {
        this._assertDestroyed("_removeAndDestroyChildren");

        let destroyOptions: PIXI.DestroyOptions = {
            children: true,
        };

        if (this.children != null && this.children.length > 0) {
            for (let i = this.children.length - 1; i >= 0; i--) {
                if (this.children[i] != null) {
                    (this.children[i] as any).destroy(destroyOptions);
                }
            }
        }

        this.removeChildren();
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public radians2degrees(radians: number): number {
        return radians * (180 / Math.PI);
    }

    /**
     * Important! The WebGL renderer only supports the NORMAL, ADD, MULTIPLY and SCREEN blend modes.
     * Anything else will silently act like NORMAL.
     *
     * @param mode
     * @returns {number}
     */
    public getBlendModeByString(mode: string): number {
        mode = mode.toString();
        switch (mode) {
            case "0":
            case "normal":
                return PIXI.BLEND_MODES.NORMAL;
            case "1":
            case "add":
                return PIXI.BLEND_MODES.ADD;
            case "2":
            case "multiply":
                return PIXI.BLEND_MODES.MULTIPLY;
            case "3":
            case "screen":
                return PIXI.BLEND_MODES.SCREEN;
            case "4":
            case "overlay":
                return PIXI.BLEND_MODES.OVERLAY;
            case "5":
            case "darken":
                return PIXI.BLEND_MODES.DARKEN;
            case "6":
            case "lighten":
                return PIXI.BLEND_MODES.LIGHTEN;
            case "7":
            case "color_dodge":
                return PIXI.BLEND_MODES.COLOR_DODGE;
            case "8":
            case "color_burn":
                return PIXI.BLEND_MODES.COLOR_BURN;
            case "9":
            case "hard_light":
                return PIXI.BLEND_MODES.HARD_LIGHT;
            case "10":
            case "soft_light":
                return PIXI.BLEND_MODES.SOFT_LIGHT;
            case "11":
            case "difference":
                return PIXI.BLEND_MODES.DIFFERENCE;
            case "12":
            case "exclusion":
                return PIXI.BLEND_MODES.EXCLUSION;
            case "13":
            case "hue":
                return PIXI.BLEND_MODES.HUE;
            case "14":
            case "saturation":
                return PIXI.BLEND_MODES.SATURATION;
            case "15":
            case "color":
                return PIXI.BLEND_MODES.COLOR;
            case "16":
            case "luminosity":
                return PIXI.BLEND_MODES.LUMINOSITY;
            default:
                if (DEBUG) {
                    this.log("ERROR Unknown blending mode " + mode + ". Used default mode NORMAL.");
                }
                return PIXI.BLEND_MODES.NORMAL;
        }
    }

    /**
     * Prints output on screen inside element with specified debug content
     */
    protected _printDebugInfo(text: any, node?: Element) {
        if (DEBUG) {
            if (this.getChildByName(ImageElementAttributes.DEBUG_INFO) != null) {
                this.removeChild(this.getChildByName(ImageElementAttributes.DEBUG_INFO));
            }
            if (
                node == undefined ||
                (node.hasAttribute(ImageElementAttributes.DEBUG_INFO) &&
                    node.getAttribute(ImageElementAttributes.DEBUG_INFO) === "true")
            ) {
                // test
                /*let border = new ShapeElement();
                 border.beginFill(0xFFFFFF, 0);
                 border.lineStyle(2, 0xff0000);
                 border.drawRect(0, 0, this._textures[0].width, this._textures[0].height);
                 this.addChild(border);*/

                let textInfo = new TextElement();
                textInfo.name = ImageElementAttributes.DEBUG_INFO;
                textInfo.x = textInfo.y = 0;
                textInfo.style.fill = 0xff0000;
                textInfo.style.fontSize = 10;
                textInfo.text = JSON.stringify(text, null, 4);

                this.addChild(textInfo);
            }
        }
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }
}

export const ImageElementAttributes = {
    /**
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    ANCHOR: "Anchor",
    /**
     * Target animation from where is specific frame selected. Attribute Frame="?" needed.
     */
    ANIMATION: "Animation",
    /**
     *
     */
    BEZIER_TWEEN_X: "BezierTweenX",
    BEZIER_TWEEN_Y: "BezierTweenY",
    BEZIER_TWEEN_AUTOROTATE: "BezierTweenAutorotate",
    BEZIER_TWEEN_EASE: "BezierTweenEase",
    BEZIER_TWEEN_DURATION: "BezierTweenDuration",
    BEZIER_TWEEN_DELAY: "BezierTweenDelay",
    BEZIER_TWEEN_LOOP: "BezierTweenLoop",
    /**
     * A ImageElement instance that defines a mask for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property BitmapMask
     * @type string
     * @default null
     */
    BITMAP_MASK: "BitmapMask",
    BITMAP_MASK_SHOW: "BitmapMaskShow",
    BITMAP_MASK_X: "BitmapMaskX",
    BITMAP_MASK_Y: "BitmapMaskY",
    /**
     * Blend mode for animation.
     * Bleding modes:
     *   0 = NORMAL;
     *   1 = ADD;
     *   2 = MULTIPLY;
     *   3 = SCREEN;
     *   4 = OVERLAY;
     *   5 = DARKEN;
     *   6 = LIGHTEN;
     *   7 = COLOR_DODGE;
     *   8 = COLOR_BURN;
     *   9 = HARD_LIGHT;
     *   10 = SOFT_LIGHT;
     *   11 = DIFFERENCE;
     *   12 = EXCLUSION;
     *   13 = HUE;
     *   14 = SATURATION;
     *   15 = COLOR;
     *   16 = LUMINOSITY;
     * @property BlendMode
     * @type number
     * @default null
     */
    BLEND_MODE: "BlendMode",
    DEBUG_INFO: "DebugInfo",
    DEBUG: "Debug",

    FRAME: "Frame",

    FROM_FRAME: "FromFrame",
    /**
     * Reference to image from resources.
     * @property Image
     * @type string
     * @default 0
     */
    IMAGE: "Image",
    /**
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type ShapeElement
     * @default null
     */
    MASK: "Mask",
    /**
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /**
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_BEZIER_AUTOROTATE: "PositionTweenBezierAutorotate",
    POSITION_TWEEN_BEZIER_X: "PositionTweenBezierX",
    POSITION_TWEEN_BEZIER_Y: "PositionTweenBezierY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /**
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_LOOP: "RotationTweenLoop",
    /**
     * Tween scale of this display object with children.
     * For ease use ScaleTweenEase (string - name of easeing function).
     * For scaling X use ScaleTweenX (consist of two values (start scaleX; end scaleX) or three values (start scaleX; middle scaleX, end scaleX)).
     * For scaling Y use ScaleTweenY (consist of two values (start scaleY; end scaleY) or three values (start scaleY; middle scaleY, end scaleY)).
     * @property ScaleTweenDuration
     * @type number - frames
     * @default null
     */
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_LOOP: "ScaleTweenLoop",
    /**
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    /**
     * The factor to stretch this display object horizontally.
     * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
     * @property ScaleX
     * @type Number
     * @default 1
     */
    SCALE_X: "ScaleX",
    /**
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    SKEW: "Skew",
    /**
     * The factor to skew this display object horizontally.
     * @property SkewX
     * @type Number
     * @default 0
     */
    SKEW_X: "SkewX",
    /**
     * The factor to skew this display object vertically.
     * @property SkewY
     * @type Number
     * @default 0
     */
    SKEW_Y: "SkewY",
    /**
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    /**
     * Tint of image. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
    /**
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /**
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /**
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ImageElement.ts
 **/